import { useQuery } from "@apollo/client/react";
import type { PlainDate } from "@sa-nu/shared-utils/date";
import { toPlainDateString, getCurrentDate } from "@sa-nu/shared-utils/date";
import { formatCurrency } from "@sa-nu/shared-utils/price";

import { graphql } from "../../../__generated__/gql";
import type { AccommodationReservationProviderInput } from "../../../__generated__/gql/graphql";

const ReservationEstimateQuery = graphql(`
  query ReservationEstimate($input: DraftAccommodationReservationInput!) {
    draftAccommodationReservation(input: $input) {
      fee {
        totalAmount
      }
    }
  }
`);

export type UseReservationEstimateResult = {
  /** 料金総額のフォーマット済み文字列。fetch 前・失敗時は null */
  total: string | null;
  loading: boolean;
  error: Error | undefined;
};

/**
 * CI/CO が両方確定した時のみ draftAccommodationReservation を fetch し、
 * 料金総額 (fee.totalAmount) を返す。
 * guestCounts は大人1名・kids/infants 0名の固定値を使用する。
 * options は全て false の固定値を使用する。
 */
export function useReservationEstimate({
  accommodationId,
  provider,
  firstNight,
  lastNight,
}: {
  accommodationId: string;
  provider: AccommodationReservationProviderInput;
  /** non-null の場合のみ fetch する */
  firstNight: PlainDate | null;
  /** non-null の場合のみ fetch する */
  lastNight: PlainDate | null;
}): UseReservationEstimateResult {
  const skip =
    firstNight === null || lastNight === null || accommodationId === "";
  // skip=true の場合は実行されないため、現在日付をプレースホルダーとして使用
  const today = getCurrentDate();
  const firstNightStr = toPlainDateString(firstNight ?? today);
  const lastNightStr = toPlainDateString(lastNight ?? today);

  const { data, loading, error } = useQuery(ReservationEstimateQuery, {
    skip,
    variables: {
      input: {
        accommodationId,
        provider,
        firstNight: firstNightStr,
        lastNight: lastNightStr,
        options: {
          bringDog: false,
          numberOfDogs: 0,
          useEvCharger: false,
        },
        guestCounts: {
          numberOfAdults: 1,
          numberOfKids: 0,
          numberOfInfants: 0,
        },
      },
    },
  });

  // skip 時は Apollo が前回の data を保持していても必ず null を返す
  const totalAmount = data?.draftAccommodationReservation?.fee?.totalAmount;
  const total =
    skip || totalAmount === undefined ? null : formatCurrency(totalAmount);

  return {
    total,
    loading,
    error,
  };
}
